import {
  type KeyboardEvent,
  type MouseEvent,
  type RefObject,
  useCallback,
  useId,
  useState,
} from 'react';

import type { ListboxOptionItem, ListboxSelection } from './listbox.constant';
import {
  edgeEnabledValue,
  findEnabledValue,
  listboxOptionId,
  useDismissOnOutsidePress,
} from './listbox.lib';

export interface UseListboxComboboxOptions {
  options: readonly ListboxOptionItem[];
  selection: ListboxSelection;
  /** 지금 선택된 값들 */
  values: readonly string[];
  /** 줄을 골랐을 때(누르기 · Enter · Space) */
  onChoose: (option: ListboxOptionItem) => void;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** 열 수 없음(disabled · init) */
  disabled?: boolean;
  /** 키보드로 옮겨 다닐 수 없음(empty · loading) */
  navigable?: boolean;
  /** 밖을 눌렀는지 가릴 요소들(트리거를 감싼 틀 · 떠 있는 목록) */
  insideRefs: ReadonlyArray<RefObject<HTMLElement | null>>;
}

/**
 * 콤보박스(트리거) + 목록의 공통 동작. Select · MultiSelect 가 쓴다.
 * - 닫힘: 누르기 · 위 · 아래 · Enter · Space 로 연다(선택된 줄, 없으면 첫 줄을 가리킨다)
 * - 열림: 위 · 아래 · Home · End 로 옮기고 Enter · Space 로 고른다. single 은 고르면 닫힌다
 * - Esc · Tab · 밖 누르기로 닫는다
 */
export function useListboxCombobox({
  options,
  selection,
  values,
  onChoose,
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  disabled = false,
  navigable = true,
  insideRefs,
}: UseListboxComboboxOptions) {
  const listboxId = useId();
  const [openState, setOpenState] = useState(defaultOpen);
  const open = !disabled && (openProp ?? openState);
  const [active, setActive] = useState<string | null>(null);

  const setOpen = useCallback(
    (next: boolean) => {
      if (openProp === undefined) setOpenState(next);
      onOpenChange?.(next);
    },
    [openProp, onOpenChange],
  );

  const openWith = (edge: 'first' | 'last') => {
    const selected = options.find((option) => values.includes(option.value) && !option.disabled);
    setActive(selected ? selected.value : edgeEnabledValue(options, edge));
    setOpen(true);
  };

  const choose = (option: ListboxOptionItem) => {
    if (option.disabled) return;
    setActive(option.value);
    onChoose(option);
    if (selection === 'single') setOpen(false);
  };

  // 제어(open prop)이고 onOpenChange 가 없으면 setOpen 은 아무것도 바꾸지 않는다(확인 페이지의 고정 열림)
  useDismissOnOutsidePress(open, insideRefs, () => setOpen(false));

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (disabled) return;
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        openWith(event.key === 'ArrowUp' ? 'last' : 'first');
      }
      return;
    }
    switch (event.key) {
      case 'Escape':
        // 목록만 닫는다(바깥 팝업까지 닫히지 않게)
        event.stopPropagation();
        setOpen(false);
        break;
      case 'Tab':
        setOpen(false);
        return;
      case 'ArrowDown':
        if (navigable) setActive(findEnabledValue(options, active, 1));
        break;
      case 'ArrowUp':
        if (navigable) setActive(findEnabledValue(options, active, -1));
        break;
      case 'Home':
        if (navigable) setActive(edgeEnabledValue(options, 'first'));
        break;
      case 'End':
        if (navigable) setActive(edgeEnabledValue(options, 'last'));
        break;
      case 'Enter':
      case ' ': {
        const option = options.find((item) => item.value === active);
        if (navigable && option) choose(option);
        else if (selection === 'single') setOpen(false);
        break;
      }
      default:
        return;
    }
    event.preventDefault();
  };

  /** 열려 있으면 닫고, 닫혀 있으면 연다 */
  const toggle = () => {
    if (disabled) return;
    if (open) setOpen(false);
    else openWith('first');
  };

  /** 마우스로 누른 것만 여닫는다(키보드가 만든 click 은 `onKeyDown` 이 이미 처리했다 — detail 0) */
  const onClick = (event: MouseEvent<HTMLElement>) => {
    if (event.detail === 0) return;
    toggle();
  };

  const activeIndex = options.findIndex((option) => option.value === active);
  const activeDescendant =
    open && navigable && activeIndex >= 0 ? listboxOptionId(listboxId, activeIndex) : undefined;

  return {
    open,
    setOpen,
    active: open ? active : null,
    setActive,
    choose,
    onKeyDown,
    onClick,
    toggle,
    listboxId,
    activeDescendant,
  };
}
